"use client";
import Link from "next/link";
import { useRef } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import CourseCard from "./course-card";
import { Button } from "@components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { motion, useInView } from "framer-motion";

type TopPerformingCoursesProps = {
	coursesData: Course[];
};

const TopPerformingCourses = ({ coursesData }: TopPerformingCoursesProps) => {
	const titleRef = useRef(null);
	const isTitleInView = useInView(titleRef, { once: true, margin: "-100px" });

	const getAllTopPerformingCourses = async () => {
		try {
			const response = await axios.get("/api/course/top-performing");
			return response?.data as Course[];
		} catch (error: unknown) {
			console.info((error as AxiosError<string>)?.response?.data || "Internal server error!");
			return [];
		}
	};

	const { data: courses } = useQuery<Course[]>({
		queryKey: ["courses"],
		queryFn: getAllTopPerformingCourses,
		initialData: coursesData,
		retry: 0,
	});

	return (
		<section className="py-12 sm:py-16">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<motion.div className="mb-8 text-center sm:mb-12" ref={titleRef} initial={{ opacity: 0, y: 30 }} animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.8 }}>
					<h2 className="text-foreground mb-3 px-2 text-2xl leading-tight font-bold sm:mb-4 sm:text-3xl md:text-4xl lg:text-4xl">Popular Courses</h2>
					<motion.p className="mx-auto max-w-2xl px-2 text-sm leading-relaxed text-gray-500 sm:text-base md:text-lg" initial={{ opacity: 0, y: 20 }} animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.3 }}>
						Explore our most popular courses designed to help you achieve your learning goals and advance your career.
					</motion.p>
				</motion.div>

				{courses.length === 0 ? (
					<div className="py-12 text-center">
						<p className="mb-4 text-gray-500">No courses available at the moment.</p>
					</div>
				) : (
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{courses.map(course => (
							<CourseCard course={course} key={course?.uuid} />
						))}
					</div>
				)}

				<motion.div className="mt-10 flex justify-center" initial={{ opacity: 0, y: 20 }} animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.8 }}>
					<Button asChild className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground w-full rounded-full border-2 px-6 py-3 transition-colors duration-300 sm:w-auto sm:px-8 sm:py-4" size="lg" variant="outline">
						<Link href="/offerings">View All Courses</Link>
					</Button>
				</motion.div>
			</div>
		</section>
	);
};

export default TopPerformingCourses;
